import React, { useState } from 'react';

const BRAND_BLUE = '#4841F0';

const SENIORITY_COLORS = {
  'Junior':    { bg: '#fef9c3', color: '#854d0e' },
  'Mid':       { bg: '#dbeafe', color: '#1d4ed8' },
  'Mid-level': { bg: '#dbeafe', color: '#1d4ed8' },
  'Senior':    { bg: '#ede9fe', color: '#6d28d9' },
  'Lead':      { bg: '#fce7f3', color: '#9d174d' },
  'Principal': { bg: '#fce7f3', color: '#9d174d' },
  'Director':  { bg: '#fee2e2', color: '#991b1b' },
};

const seniorityStyle = (s) =>
  SENIORITY_COLORS[s] || { bg: '#f3f4f6', color: '#374151' };

const formatSalary = (salary) => {
  if (!salary || !salary.amount) return null;
  const { amount, currency } = salary;
  const symbol = currency === 'USD' ? '$' : currency === 'GBP' ? '£' : '€';
  if (amount >= 1000) {
    return `${symbol}${(amount / 1000).toFixed(0)}k`;
  }
  return `${symbol}${amount}`;
};

const ClientRoles = ({ data, expanded = false }) => {
  const [openRole, setOpenRole] = useState(null);

  // Group by specific role title, falling back to department
  const roleStats = {};
  data.forEach(candidate => {
    const key = candidate.role || candidate.department || 'Unknown';
    if (!roleStats[key]) {
      roleStats[key] = {
        name: key,
        department: candidate.department || '',
        total: 0, hired: 0, rejected: 0, inProgress: 0,
        avgTime: [],
        salaries: [],
        seniorities: new Set(),
        candidates: [],
      };
    }
    const r = roleStats[key];
    r.total += 1;
    r.candidates.push(candidate);
    if (candidate.seniority) r.seniorities.add(candidate.seniority);
    if (candidate.stage === 'Hired') {
      r.hired += 1;
      const t = parseInt(candidate.timeToHire) || 0;
      if (t > 0) r.avgTime.push(t);
      if (candidate.salary?.amount > 0) r.salaries.push(candidate.salary);
    } else if (candidate.stage === 'Rejected') {
      r.rejected += 1;
    } else {
      r.inProgress += 1;
    }
  });

  const roles = Object.values(roleStats)
    .map(r => ({
      ...r,
      seniorities: [...r.seniorities],
      avgTimeToHire: r.avgTime.length > 0
        ? Math.round(r.avgTime.reduce((a, b) => a + b, 0) / r.avgTime.length)
        : null,
      avgSalary: r.salaries.length > 0
        ? { amount: Math.round(r.salaries.reduce((a, b) => a + b.amount, 0) / r.salaries.length), currency: r.salaries[0].currency }
        : null,
    }))
    .sort((a, b) => b.total - a.total);

  if (roles.length === 0) {
    return (
      <div className={`dashboard-card p-6 slide-in-up ${expanded ? 'col-span-full' : ''}`}>
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-lg font-semibold text-gray-800">Active Roles</h3>
          <div className="bg-amber-100 text-amber-800 text-xs font-medium px-2.5 py-1 rounded-full">Roles</div>
        </div>
        <p className="text-sm text-gray-600 mb-4">Roles with open or recent hiring activity</p>
        <div className="flex items-center justify-center h-48 text-gray-400 text-sm">
          No role data available yet
        </div>
      </div>
    );
  }

  return (
    <div className={`dashboard-card p-6 slide-in-up ${expanded ? 'col-span-full' : ''}`}>
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-lg font-semibold text-gray-800">Active Roles</h3>
        <div className="bg-amber-100 text-amber-800 text-xs font-medium px-2.5 py-1 rounded-full">
          {roles.length} role{roles.length !== 1 ? 's' : ''}
        </div>
      </div>
      <p className="text-sm text-gray-600 mb-4">Roles with open or recent hiring activity</p>

      <div className={`space-y-3 ${expanded ? '' : 'max-h-[420px] overflow-y-auto pr-1'}`}>
        {roles.map((role) => {
          const isOpen = openRole === role.name;
          return (
            <div
              key={role.name}
              className="border border-gray-100 rounded-lg overflow-hidden hover:border-gray-200 transition-colors"
            >
              {/* Role header */}
              <button
                className="w-full text-left p-3 focus:outline-none"
                onClick={() => setOpenRole(isOpen ? null : role.name)}
              >
                <div className="flex items-start justify-between gap-2 mb-1.5">
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-semibold text-gray-800 leading-tight">{role.name}</h4>
                    {role.department && role.department !== role.name && (
                      <p className="text-xs text-gray-400 mt-0.5">{role.department}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    {role.seniorities.map(s => (
                      <span key={s} className="text-xs font-medium px-2 py-0.5 rounded-full"
                        style={{ background: seniorityStyle(s).bg, color: seniorityStyle(s).color }}>
                        {s}
                      </span>
                    ))}
                    <span className="text-xs font-medium px-2 py-0.5 rounded-full" style={{
                      background: role.inProgress > 0 ? '#eef2ff' : '#f0fdf4',
                      color: role.inProgress > 0 ? BRAND_BLUE : '#16a34a',
                    }}>
                      {role.inProgress > 0 ? `${role.inProgress} active` : 'Completed'}
                    </span>
                  </div>
                </div>

                {/* Stats row */}
                <div className="grid grid-cols-5 gap-1 text-center">
                  <div>
                    <p className="text-sm font-semibold text-gray-800">{role.total}</p>
                    <p className="text-xs text-gray-400">Total</p>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-emerald-600">{role.hired}</p>
                    <p className="text-xs text-gray-400">Hired</p>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-red-500">{role.rejected}</p>
                    <p className="text-xs text-gray-400">Rejected</p>
                  </div>
                  <div>
                    <p className="text-sm font-semibold" style={{ color: BRAND_BLUE }}>
                      {role.avgTimeToHire ? `${role.avgTimeToHire}d` : '—'}
                    </p>
                    <p className="text-xs text-gray-400">Avg Time</p>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-700">
                      {role.avgSalary ? formatSalary(role.avgSalary) : '—'}
                    </p>
                    <p className="text-xs text-gray-400">Avg Salary</p>
                  </div>
                </div>

                {/* Progress bar */}
                {role.total > 0 && (
                  <div className="mt-2.5 w-full bg-gray-100 rounded-full h-1.5 flex overflow-hidden">
                    {role.hired > 0 && (
                      <div className="h-full bg-emerald-500 rounded-l-full"
                        style={{ width: `${(role.hired / role.total) * 100}%` }} />
                    )}
                    {role.inProgress > 0 && (
                      <div className="h-full"
                        style={{ width: `${(role.inProgress / role.total) * 100}%`, background: BRAND_BLUE }} />
                    )}
                    {role.rejected > 0 && (
                      <div className="h-full bg-red-400 rounded-r-full"
                        style={{ width: `${(role.rejected / role.total) * 100}%` }} />
                    )}
                  </div>
                )}
              </button>

              {/* Candidate list — always shown in expanded view, toggled in compact */}
              {(isOpen || expanded) && (
                <div className="border-t border-gray-50 bg-gray-50/50 px-3 pb-3 pt-2">
                  <div className="space-y-1.5">
                    {role.candidates.map((c, i) => (
                      <div key={i} className="flex items-center justify-between gap-2 text-xs py-1 border-b border-gray-100 last:border-0">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="font-medium text-gray-700 truncate">{c.name || 'Unnamed'}</span>
                          {c.seniority && (
                            <span className="px-1.5 py-0.5 rounded text-xs font-medium flex-shrink-0"
                              style={{ background: seniorityStyle(c.seniority).bg, color: seniorityStyle(c.seniority).color }}>
                              {c.seniority}
                            </span>
                          )}
                          {c.source && (
                            <span className="text-gray-400 flex-shrink-0 hidden sm:inline">via {c.source}</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {c.salary?.amount > 0 && (
                            <span className="text-gray-500 font-medium">{formatSalary(c.salary)}</span>
                          )}
                          <span className={`px-2 py-0.5 rounded-full font-medium ${
                            c.stage === 'Hired'    ? 'bg-emerald-50 text-emerald-700' :
                            c.stage === 'Rejected' ? 'bg-red-50 text-red-700' :
                                                     'bg-blue-50 text-blue-700'
                          }`}>
                            {c.stage || 'Unknown'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ClientRoles;
